import { supabase } from '@/integrations/supabase/client';
import { Cota, CreateCotaDto, UpdateCotaDto, CotaWithRelations } from '@/features/cotas/types';

export class CotasSupabaseService {
  async findAll(): Promise<Cota[]> {
    const { data, error } = await supabase
      .from('cotas')
      .select('*')
      .order('numero_cota', { ascending: true });

    if (error) throw error;
    return (data || []) as unknown as Cota[];
  }

  async findByGrupo(grupoId: number): Promise<Cota[]> {
    const { data, error } = await supabase
      .from('cotas')
      .select('*')
      .eq('grupo_id', grupoId)
      .order('numero_cota', { ascending: true });

    if (error) throw error;
    return (data || []) as unknown as Cota[];
  }

  async findById(id: string | number): Promise<Cota | null> {
    const { data, error } = await supabase
      .from('cotas')
      .select('*')
      .eq('id', typeof id === 'string' ? parseInt(id) : id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data as unknown as Cota;
  }

  async findByIdWithRelations(id: string | number): Promise<CotaWithRelations | null> {
    const { data, error } = await supabase
      .from('cotas')
      .select(`
        *,
        grupo:grupos(id, numero_grupo, especie),
        cliente:clientes(id, nome, cpf),
        contrato:contratos(id, contrato_numero, status)
      `)
      .eq('id', typeof id === 'string' ? parseInt(id) : id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data as unknown as CotaWithRelations;
  }

  async create(data: CreateCotaDto): Promise<Cota> {
    const { data: result, error } = await supabase
      .from('cotas')
      .insert(data as any)
      .select()
      .single();

    if (error) throw error;
    return result as unknown as Cota;
  }

  async update(id: string | number, data: UpdateCotaDto): Promise<Cota> {
    const { data: result, error } = await supabase
      .from('cotas')
      .update(data as any)
      .eq('id', typeof id === 'string' ? parseInt(id) : id)
      .select()
      .single();

    if (error) throw error;
    return result as unknown as Cota;
  }

  async delete(id: string | number): Promise<void> {
    const { error } = await supabase
      .from('cotas')
      .delete()
      .eq('id', typeof id === 'string' ? parseInt(id) : id);

    if (error) throw error;
  }

  async ocuparCota(id: string | number, clienteId: string | number, contratoId: string | number): Promise<Cota> {
    const { data: result, error } = await supabase
      .from('cotas')
      .update({
        status: 'OCUPADA',
        cliente_id: typeof clienteId === 'string' ? parseInt(clienteId) : clienteId,
        contrato_id: typeof contratoId === 'string' ? parseInt(contratoId) : contratoId,
        data_ocupacao: new Date().toISOString()
      })
      .eq('id', typeof id === 'string' ? parseInt(id) : id)
      .select()
      .single();

    if (error) throw error;
    return result as unknown as Cota;
  }

  async liberarCota(id: string | number): Promise<Cota> {
    const { data: result, error } = await supabase
      .from('cotas')
      .update({
        status: 'DISPONIVEL',
        cliente_id: null,
        contrato_id: null,
        data_ocupacao: null
      })
      .eq('id', typeof id === 'string' ? parseInt(id) : id)
      .select()
      .single();

    if (error) throw error;
    return result as unknown as Cota;
  }

  async contemplarCota(id: string | number): Promise<Cota> {
    const { data: result, error } = await supabase
      .from('cotas')
      .update({
        status: 'CONTEMPLADA',
        data_contemplacao: new Date().toISOString()
      })
      .eq('id', typeof id === 'string' ? parseInt(id) : id)
      .select()
      .single();

    if (error) throw error;
    return result as unknown as Cota;
  }

  async quitarCota(id: string | number): Promise<Cota> {
    const { data: result, error } = await supabase
      .from('cotas')
      .update({
        status: 'QUITADA',
        data_quitacao: new Date().toISOString()
      })
      .eq('id', typeof id === 'string' ? parseInt(id) : id)
      .select()
      .single();

    if (error) throw error;
    return result as unknown as Cota;
  }

  async findByTenantId(tenantId: number): Promise<Cota[]> {
    const { data, error } = await supabase
      .from('cotas')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('numero_cota', { ascending: true });

    if (error) throw error;
    return (data || []) as unknown as Cota[];
  }

  async findByStatus(status: string): Promise<Cota[]> {
    const { data, error } = await supabase
      .from('cotas')
      .select('*')
      .eq('status', status)
      .order('numero_cota', { ascending: true });

    if (error) throw error;
    return (data || []) as unknown as Cota[];
  }

  async findByGrupoWithRelations(grupoId: number): Promise<CotaWithRelations[]> {
    const { data, error } = await supabase
      .from('cotas')
      .select(`
        *,
        grupo:grupos(id, numero_grupo, especie),
        cliente:clientes(id, nome, cpf),
        contrato:contratos(id, contrato_numero, status)
      `)
      .eq('grupo_id', grupoId)
      .order('numero_cota', { ascending: true });

    if (error) throw error;
    return (data || []) as unknown as CotaWithRelations[];
  }
}

// Instância singleton
export const cotasSupabaseService = new CotasSupabaseService();
